import { useEffect, useId, type ReactNode } from "react";
import {createPortal} from 'react-dom';
import {useOverlayViewport} from '@/hooks/useOverlayViewport';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
}

export default function Modal({ open, onClose, title, children, footer }: ModalProps) {
  const viewport=useOverlayViewport();
  const titleId=useId();
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);
  useEffect(()=>{
    if(!open)return;
    const close=(event:KeyboardEvent)=>{if(event.key==='Escape')onClose();};
    document.addEventListener('keydown',close);
    return()=>document.removeEventListener('keydown',close);
  },[open,onClose]);

  if (!open) return null;

  return createPortal(
    <div style={{...viewport,padding:'max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left))'}} className="fixed z-50 flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onClose}
        className="absolute inset-0 cursor-pointer bg-foreground-950/45 animate-fade-in"
      />
      <div className="relative flex max-h-full min-h-0 min-w-0 w-full max-w-[480px] flex-col overflow-hidden rounded-3xl bg-background-50 animate-scale-in">
        <div className="flex max-h-[35%] shrink-0 items-center justify-between gap-3 overflow-y-auto border-b border-background-200 px-5 py-4">
          <h3 id={titleId} className="min-w-0 break-words font-heading text-base font-bold text-foreground-950">
            {title ?? ""}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-700 transition-colors hover:bg-background-200"
          >
            <i className="ri-close-line text-lg leading-none" />
          </button>
        </div>
        <div className="no-scrollbar min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain [overflow-wrap:anywhere]">{children}</div>
        {footer ? (
          <div className="max-h-[45%] shrink-0 overflow-y-auto overscroll-contain border-t border-background-200 bg-background-50 px-5 py-4">{footer}</div>
        ) : null}
      </div>
    </div>,document.body
  );
}
